<template>
  <div class="space-y-6">
    <div>
      <h3 class="text-lg font-semibold text-gray-900 mb-1">
        Meals from {{ dateRange.start_date }} to {{ dateRange.end_date }}
      </h3>
      <p class="text-gray-600 text-sm mb-4">
        Uncheck any meals you want to exclude from the shopping list.
      </p>

      <div v-if="loading" class="flex justify-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>

      <div v-else-if="meals.length === 0" class="text-center py-8 text-gray-500">
        <p>No meals found for the selected date range.</p>
      </div>

      <!-- Portions mode summary note -->
      <div v-if="hasMultiDayMeals" class="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600 space-y-1">
        <p class="font-semibold text-gray-700">Multi-day meals in this range:</p>
        <div class="flex flex-wrap gap-3 mt-1">
          <span v-if="leftoverCount > 0" class="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded bg-green-50 text-green-800 border border-green-200">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1.001A3.75 3.75 0 0012 18z" />
            </svg>
            {{ leftoverCount }} cooked once — ingredients counted once
          </span>
          <span v-if="repeatCount > 0" class="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded bg-blue-50 text-blue-800 border border-blue-200">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
            {{ repeatCount }} cook-fresh — ingredients × days
          </span>
        </div>
      </div>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="meal in meals"
          :key="meal.id"
          class="flex items-start gap-3 border border-gray-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
          :class="{ 'opacity-50': !includedIds.has(meal.id) }"
          @click="toggleMeal(meal.id)"
        >
          <button
            type="button"
            class="flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200"
            :class="includedIds.has(meal.id)
              ? 'bg-primary-500 border-primary-500 hover:bg-primary-600'
              : 'border-gray-300 hover:border-primary-500 hover:bg-primary-50'"
            @click.stop="toggleMeal(meal.id)"
          >
            <svg v-if="includedIds.has(meal.id)" class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </button>
          <div class="min-w-0">
            <h4 class="font-medium text-gray-900">{{ meal.title }}</h4>
            <p v-if="meal.description" class="text-sm text-gray-600 mt-1 truncate">{{ meal.description }}</p>
            <p class="text-sm text-blue-600 mt-2">{{ formatDate(meal.scheduled_date) }}</p>
          </div>
        </div>
      </div>

      <div v-if="hasMultiDayMeals" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="meal in meals"
          :key="meal.id"
          class="flex items-start gap-3 border rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
          :class="[
            includedIds.has(meal.id) ? 'border-gray-200' : 'opacity-50 border-gray-200',
            (meal.portions_days ?? 1) > 1 && meal.portions_mode === 'repeat' ? 'bg-blue-50/40' : '',
            (meal.portions_days ?? 1) > 1 && meal.portions_mode !== 'repeat' ? 'bg-green-50/40' : '',
          ]"
          @click="toggleMeal(meal.id)"
        >
          <button
            type="button"
            class="flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200"
            :class="includedIds.has(meal.id)
              ? 'bg-primary-500 border-primary-500 hover:bg-primary-600'
              : 'border-gray-300 hover:border-primary-500 hover:bg-primary-50'"
            @click.stop="toggleMeal(meal.id)"
          >
            <svg v-if="includedIds.has(meal.id)" class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </button>
          <div class="min-w-0 flex-1">
            <h4 class="font-medium text-gray-900">{{ meal.title }}</h4>
            <p v-if="meal.description" class="text-sm text-gray-600 mt-0.5 truncate">{{ meal.description }}</p>
            <!-- Dates display -->
            <div class="mt-2 flex flex-wrap gap-1">
              <template v-if="meal.scheduled_dates?.length > 1">
                <span v-for="d in meal.scheduled_dates" :key="d"
                  class="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                  {{ formatDate(d) }}
                </span>
              </template>
              <span v-else class="text-xs text-gray-500">{{ formatDate(meal.scheduled_date) }}</span>
            </div>
            <!-- Portions mode badge -->
            <div v-if="(meal.portions_days ?? 1) > 1" class="mt-2">
              <span class="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded"
                :class="meal.portions_mode === 'repeat'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-green-100 text-green-700'">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path v-if="meal.portions_mode === 'repeat'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                  <template v-else>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1.001A3.75 3.75 0 0012 18z" />
                  </template>
                </svg>
                {{ meal.portions_days }}d ·
                {{ meal.portions_mode === 'repeat' ? 'Cook fresh each day' : 'Cooked Once' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation buttons -->
    <div class="flex justify-between mt-6">
      <button
        @click="$emit('previous')"
        class="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
      >
        Previous
      </button>
      <button
        @click="handleNext"
        class="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        :disabled="loading"
      >
        Next: Review Ingredients
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import dayjs from 'dayjs'

interface Props {
  meals: any[]
  loading: boolean
  dateRange: {
    start_date: string
    end_date: string
  }
}

const props = defineProps<Props>()

const emit = defineEmits<{
  previous: []
  next: [excludedMealIds: string[]]
}>()

const includedIds = ref(new Set<string>())

const multiDayMeals = computed(() => props.meals.filter(m => (m.portions_days ?? 1) > 1))
const hasMultiDayMeals = computed(() => multiDayMeals.value.length > 0)
const leftoverCount = computed(() => multiDayMeals.value.filter(m => m.portions_mode !== 'repeat').length)
const repeatCount = computed(() => multiDayMeals.value.filter(m => m.portions_mode === 'repeat').length)

// Initialise when meals load
watch(() => props.meals, (meals) => {
  includedIds.value = new Set(meals.map(m => m.id))
}, { immediate: true })

const toggleMeal = (id: string) => {
  if (includedIds.value.has(id)) {
    includedIds.value.delete(id)
  } else {
    includedIds.value.add(id)
  }
  includedIds.value = new Set(includedIds.value)
}

const handleNext = () => {
  const excludedMealIds = props.meals
    .map(m => m.id)
    .filter(id => !includedIds.value.has(id))
  emit('next', excludedMealIds)
}

const formatDate = (date: string) => {
  return dayjs(date).format('DD/MM/YYYY')
}
</script>
